'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { Blog } from '@eliadmin/core/src/api/blogs/blogs-types';
import { Card, CardContent } from '@eliadmin/core/src/components/ui/card';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import type { ComponentProps } from 'react';

export type BlogCardProps = Omit<ComponentProps<typeof Card>, 'className'> & {
  className?: {
    main?: string;
    link?: string;
    image?: string;
    title?: string;
    date?: string;
    contentContainer?: string;
    content?: string;
  };
  blog: Blog;
};

export function BlogCard({ blog, className, ...props }: BlogCardProps) {
  const locale = useLocaleStore((state) => state.currentLocale);

  return (
    <Card
      {...props}
      className={cn(
        'relative w-full rounded-none border-none bg-transparent',
        'ea-blog-card',
        className?.main,
      )}
    >
      <Link
        href={ROUTES.BLOG_DETAIL({ slug: blog.slug })}
        className={cn('ea-blog-card__link h-full', className?.link)}
        prefetch={false}
      >
        <CardContent
          className={cn(
            'flex h-full flex-col rounded-none p-4',
            'ea-blog-card__content',
            className?.contentContainer,
          )}
        >
          <div className="relative h-[200px] w-full bg-primary">
            <Image
              src={blog?.image?.url ?? '/images/placeholder.png'}
              alt={resolveImageAlt(blog?.image, blog?.name, 'image')}
              fill
              style={{ objectFit: 'cover' }}
              sizes="320px"
              quality={80}
            />
          </div>

          <div
            className={cn(
              'flex flex-col',
              'ea-blog-card__content-container',
              className?.contentContainer,
            )}
          >
            <div className={cn('my-2 text-sm text-[#565656]', 'ea-blogcard_date')}>
              {new Date(blog.created_at)?.toLocaleDateString(locale)}
            </div>
            <h3
              data-ea-visual-tag="h6"
              className={cn(
                'line-clamp-2 text-start font-bold',
                'ea-blog-card__title',
                className?.title,
              )}
            >
              {blog.name}
            </h3>
          </div>
        </CardContent>
      </Link>
    </Card>
  );
}
